import { ProviderDetailsAPIErrors } from 'app/ProviderSearch/constants'
import { getSlotsStartEndDate } from 'app/ProviderSearch/utils'
import { BACKEND_SUPPORTED_PAGINATION_LIMIT } from 'constants/api'
import { DEFAULT_SLOT_LIST_LIMIT } from 'constants/common'
import { formatInTimeZone } from 'date-fns-tz'
import callApi from 'utils/callApi'
import {
  getProviderRoute,
  getProviderTestimonialsRoute,
  PROVIDER_FILTERS,
  getProviderForPracticeRoute,
  MICROLINK_BASE_URL,
  getGeoTimezone,
  PROVIDERS,
  COMMON_SPECIALITIES,
  getFiltersByTypeAndStateRoute,
} from './routes'
import enUS from 'date-fns/locale/en-US'
import qs from 'qs'

export enum RouterFilters {
  AVAILABILITY_TYPE = 'availabilityType[]',
  AREA_OF_CONCERN = 'areaOfConcern[]',
  GENDER = 'gender[]',
  CLIENT_AGE_GROUP = 'clientAgeGroup[]',
  LANGUAGE = 'language[]',
  COUNSELING_APPROACH = 'counselingApproach[]',
  COUNSELING_TYPE = 'counselingType[]',
  TIME_OF_DAY = 'timeOfDay[]',
  DAY_OF_WEEK = 'dayOfWeek[]',
  INSURANCE_ID = 'insuranceId',
}

export enum ProviderFilterTypes {
  AVAILABILITY_TYPE = 'availability_type',
  AREA_OF_CONCERN = 'area_of_concern',
  LANGUAGE = 'language',
  COUNSELING_APPROACH = 'counseling_approach',
  CLIENT_AGE_GROUP = 'client_age_group',
  TIME_OF_DAY = 'time_of_day',
  DAY_OF_WEEK = 'day_of_week',
  GENDER = 'gender',
  COUNSELING_TYPE = 'counseling_type',
  TAG = 'tag',
  ORDER_BY = 'orderBy',
  INSURANCE_ID = 'insurance_id',
}

export enum ProviderHeaderFilterTypes {
  LOCATION = 'location',
  INSURANCE = 'insurance',
  B2B_CLIENT = 'b2b_client',
}

export const providerFilterKeyMapping = {
  [RouterFilters.AVAILABILITY_TYPE]: ProviderFilterTypes.AVAILABILITY_TYPE,
  [RouterFilters.AREA_OF_CONCERN]: ProviderFilterTypes.AREA_OF_CONCERN,
  [RouterFilters.GENDER]: ProviderFilterTypes.GENDER,
  [RouterFilters.CLIENT_AGE_GROUP]: ProviderFilterTypes.CLIENT_AGE_GROUP,
  [RouterFilters.LANGUAGE]: ProviderFilterTypes.LANGUAGE,
  [RouterFilters.COUNSELING_APPROACH]: ProviderFilterTypes.COUNSELING_APPROACH,
  [RouterFilters.COUNSELING_TYPE]: ProviderFilterTypes.COUNSELING_TYPE,
  [RouterFilters.TIME_OF_DAY]: ProviderFilterTypes.TIME_OF_DAY,
  [RouterFilters.DAY_OF_WEEK]: ProviderFilterTypes.DAY_OF_WEEK,
  [RouterFilters.INSURANCE_ID]: ProviderFilterTypes.INSURANCE_ID,
}

export const getProvider = (
  queryObject: ProviderProfileQueryParams
): Promise<Provider> =>
  callApi<Provider>(
    {
      endpoint: getProviderRoute(queryObject),
      method: 'get',
    },
    (response) => {
      if (response.status === 204) {
        throw new Error(ProviderDetailsAPIErrors.PROVIDER_DELETED)
      }

      return response.data
    }
  )

export const getAllProviderDetails = (
  queryObject: ProviderProfileQueryParams
): APIResponse<ListCardProvider[]> => {
  const qsQuery = qs.stringify(queryObject, {
    addQueryPrefix: true,
    skipNulls: true,
    arrayFormat: 'repeat',
  })

  return callApi<APIResponseSchema<ListCardProvider[]>>({
    endpoint: `${PROVIDERS}${qsQuery}`,
    method: 'get',
  })
}

export type CommonSpeciality = {
  id: string
  value: string
  providerCount: number
}

export const getCommonSpecialitiesByState = (
  stateCode: string
): Promise<CommonSpeciality[]> => {
  return callApi<CommonSpeciality[]>({
    endpoint: `${COMMON_SPECIALITIES}?stateCode=${stateCode}`,
    method: 'get',
  })
}

export const getFilters = (state?: string): APIResponse<ProviderFilter[]> => {
  return callApi<APIResponseSchema<ProviderFilter[]>>({
    endpoint: `${PROVIDER_FILTERS}${state ? `?stateCode=${state}` : ''}`,
    method: 'get',
  })
}

export const getFiltersByTypeAndState = (
  type: ProviderFilterTypes,
  stateCode: string
): Promise<ProviderFilter[]> =>
  callApi<ProviderFilter[]>({
    endpoint: getFiltersByTypeAndStateRoute(type, stateCode),
    method: 'get',
  })

export const getProvidersForPractice = (
  practiceId: string
): APIResponse<ProviderMetadataForPractice[]> => {
  return callApi<APIResponseSchema<ProviderMetadataForPractice[]>>({
    endpoint: getProviderForPracticeRoute(practiceId),
    method: 'get',
  })
}

export type AvailableProvidersForPracticeParams = {
  practice: Pick<Practice, 'id' | 'latitude' | 'longitude' | 'state'>
  timeZone: string
}

const mapListCardProviderToPracticeMetadata = (
  provider: ListCardProvider
): ProviderMetadataForPractice => ({
  id: provider.id,
  firstName: provider.firstName,
  lastName: provider.lastName,
  profileImagePath: provider.profileImagePath,
  practiceId: provider.practice?.id,
  availabilityTypeIds: provider.availabilityTypeIds,
  title: provider.title,
  pronouns: provider.pronouns,
})

/**
 * Providers at a practice who pass the same availability filters as provider search
 * (is_available + bookable slots in the default date window).
 */
export const getAvailableProvidersForPractice = async ({
  practice,
  timeZone,
}: AvailableProvidersForPracticeParams): Promise<
  APIResponseSchema<ProviderMetadataForPractice[]>
> => {
  const { startDate, endDate } = getSlotsStartEndDate(
    new Date(),
    DEFAULT_SLOT_LIST_LIMIT,
    timeZone
  )

  const baseQueryParams = {
    practiceId: practice.id,
    latitude: practice.latitude,
    longitude: practice.longitude,
    state: practice.state,
    slotStartDate: startDate.toISOString(),
    slotEndDate: endDate.toISOString(),
    limit: BACKEND_SUPPORTED_PAGINATION_LIMIT,
  }

  const MAX_PAGINATION_PAGES = 50
  const mergedProviders: ListCardProvider[] = []
  let offset = 0
  let pagesFetched = 0
  let lastPagination: PaginationSchema | undefined

  while (pagesFetched < MAX_PAGINATION_PAGES) {
    pagesFetched += 1
    const query = qs.stringify(
      { ...baseQueryParams, offset },
      { addQueryPrefix: true, skipNulls: true, arrayFormat: 'repeat' }
    )

    const response = await callApi<APIResponseSchema<ListCardProvider[]>>({
      endpoint: `${PROVIDERS}${query}`,
      method: 'get',
    })

    const page = response.result ?? []
    mergedProviders.push(...page)
    lastPagination = response.pagination

    if (page.length === 0) {
      break
    }

    const totalCount = lastPagination?.count
    const isLastPage =
      page.length < BACKEND_SUPPORTED_PAGINATION_LIMIT ||
      (totalCount !== undefined &&
        totalCount !== null &&
        mergedProviders.length >= totalCount)

    if (isLastPage) {
      break
    }

    offset += BACKEND_SUPPORTED_PAGINATION_LIMIT
  }

  const result = mergedProviders.map(mapListCardProviderToPracticeMetadata)

  return {
    result,
    pagination: lastPagination,
  }
}

export const getProviderTestimonials = (
  providerId: string,
  params: { offset: number; limit: number }
): APIResponse<ProviderTestimonialApiItem[]> =>
  callApi<APIResponseSchema<ProviderTestimonialApiItem[]>>({
    endpoint: getProviderTestimonialsRoute(
      providerId,
      params.offset,
      params.limit
    ),
    method: 'get',
  })

export const getBlogMeta = (url: string): Promise<BlogMetaResponse> => {
  return callApi<BlogMetaResponse>({
    baseURL: MICROLINK_BASE_URL,
    endpoint: '',
    method: 'get',
    params: { url },
  })
}

export const fetchTimezoneFromLocation = async (
  latitude: string,
  longitude: string
): Promise<TimeZoneData> => {
  try {
    const geoTimezoneResponse = await callApi<string[]>({
      endpoint: getGeoTimezone(latitude, longitude),
    })
    const timeZoneId = geoTimezoneResponse[0]
    const timeZoneName = formatInTimeZone(new Date(), timeZoneId, 'zzzz', {
      locale: enUS,
    })

    return {
      timeZoneId,
      timeZoneName,
    }
  } catch (error) {
    return null
  }
}
